import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { listAdminPages, putAdminPage, getPublicSiteSettings } from "@/lib/adminApi";
import { missingRequiredPages, apiErrorText } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

const LABEL = { impressum: "Impressum", datenschutz: "Datenschutz", agb: "AGB", widerruf: "Widerruf" };

function PageEditor({ page, onSaved }) {
  const [title, setTitle] = useState(page.title || "");
  const [body, setBody] = useState(page.body || "");
  const [published, setPublished] = useState(!!page.published);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Başlık gerekli"); return; }
    setBusy(true);
    try {
      await putAdminPage(page.slug, { title: title.trim(), body, published });
      toast.success("Sayfa kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-md border p-4" data-testid={`page-editor-${page.slug}`}>
      <div className="space-y-1.5">
        <Label htmlFor={`pt-${page.slug}`}>Başlık</Label>
        <Input id={`pt-${page.slug}`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`pb-${page.slug}`}>İçerik (düz metin)</Label>
        <Textarea id={`pb-${page.slug}`} value={body} onChange={(e) => setBody(e.target.value)} rows={16} maxLength={50000} className="font-mono text-sm" />
        <p className="text-xs text-muted-foreground">
          Başlık için satırı &quot;## Başlık&quot; ile başlatın; paragrafları boş satırla ayırın. HTML kullanılmaz (&lt; karakteri silinir).
        </p>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={published} onCheckedChange={setPublished} data-testid={`page-publish-${page.slug}`} /> Yayında
        </label>
        <Button onClick={save} disabled={busy}>Kaydet</Button>
      </div>
    </div>
  );
}

export default function Pages() {
  useNoindex();
  const [pages, setPages] = useState(null);
  const [legalLive, setLegalLive] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([listAdminPages(), getPublicSiteSettings()]);
      setPages(p);
      setLegalLive(!!s.legal_live);
    } catch (e) {
      toast.error(apiErrorText(e, "Sayfalar yüklenemedi"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const missing = missingRequiredPages(pages);
  const current = pages && pages.find((p) => p.slug === active);

  return (
    <main className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-4 text-xl font-semibold">Sayfalar</h1>
      {pages && (
        <div
          data-testid="legal-banner"
          className={`mb-4 rounded-md border p-3 text-sm ${legalLive ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
        >
          <strong>Yasal sayfalar yayında: {legalLive ? "evet" : "hayır"}</strong>
          {missing.length > 0 && (
            <div className="mt-1">Eksik (yayınlanmamış) zorunlu sayfalar: {missing.map((s) => LABEL[s]).join(", ")}</div>
          )}
        </div>
      )}
      {!pages && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {pages && (
        <ul className="mb-4 space-y-2">
          {pages.map((p) => (
            <li key={p.slug}>
              <button
                onClick={() => setActive(p.slug === active ? null : p.slug)}
                className="flex w-full items-center justify-between rounded-md border p-3 text-left hover:bg-secondary"
                data-testid={`page-row-${p.slug}`}
              >
                <span className="text-sm font-medium">{LABEL[p.slug]} <span className="text-muted-foreground">/{p.slug}</span></span>
                <Badge variant={p.published ? "default" : "outline"}>{p.published ? "Yayında" : "Taslak"}</Badge>
              </button>
              {current && current.slug === p.slug && (
                <div className="mt-2"><PageEditor key={p.slug + (p.updated_at || "")} page={p} onSaved={load} /></div>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
